import {
  DEFAULT_INITIAL_MESSAGE,
  DEFAULT_MODEL,
  DEFAULT_PROMPT,
  DEFAULT_SPEECH_RECOGNITION_LANGUAGE_CODE,
  DEFAULT_VOICE,
  models,
} from '@3d-lib/constants';
import {
  MainStateDispatch,
  Model,
  SettingsType,
  SpeechRecognitionLanguageCode,
  Voice,
} from '@3d-lib/types';
import { isGoogleVoice, voiceNames, voices } from '@3d-lib/voices';
import { FC } from 'react';
import styled from 'styled-components';
import { Modal } from './Modal';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';

type SettingsModalContentProps = {
  mainStateDispatch: MainStateDispatch;
  settings: SettingsType;
  hasGoogleApiKey: boolean;
};

const SettingsModalContent: FC<SettingsModalContentProps> = ({
  mainStateDispatch,
  settings,
  hasGoogleApiKey,
}) => {
  const { t } = useTranslation();

  const currentModel = settings.model;
  const currentVoice = settings.voice;
  const currentPrompt = settings.prompt;
  const currentInitialMessage = settings.initialMessage;
  const currentSpeechRecognitionLanguageCode =
    settings.speechRecognitionLanguageCode;

  const handleSettingsChange = (settings: SettingsType) => {
    mainStateDispatch({
      type: 'SET_SETTINGS',
      payload: settings,
    });
  };

  // Function to get the current URL with updated settings as query parameters
  const getShareableURL = () => {
    const params = new URLSearchParams();

    if (settings.model !== DEFAULT_MODEL) {
      // TODO: Find a way to remove these "as Model" assertions.
      params.set('model', settings.model as Model);
    }
    if (settings.voice !== DEFAULT_VOICE) {
      params.set('voice', settings.voice);
    }
    if (settings.prompt !== DEFAULT_PROMPT) {
      params.set('prompt', settings.prompt);
    }
    if (settings.initialMessage !== DEFAULT_INITIAL_MESSAGE) {
      params.set('initialMessage', settings.initialMessage);
    }
    if (
      settings.speechRecognitionLanguageCode !==
      DEFAULT_SPEECH_RECOGNITION_LANGUAGE_CODE
    ) {
      params.set('speechLang', settings.speechRecognitionLanguageCode);
    }

    const paramsString = params.toString();
    const url = `${window.location.origin}${window.location.pathname}`;

    return paramsString ? `${url}?${params.toString()}` : url;
  };

  return (
    <SettingsContainer>
      <SettingRow>
        <label style={{ margin: 'auto 0' }}>
          {t('avatar.configs.voice', 'Voice')}:
        </label>
        <SettingsSelect
          value={currentVoice}
          onChange={(e) =>
            handleSettingsChange({
              ...settings,
              voice: e.target.value as Voice,
            })
          }
        >
          {voiceNames.map((voiceName) => (
            <option key={voiceName} value={voiceName}>
              {voices[voiceName].gender}: {voiceName} (
              {voices[voiceName].provider})
            </option>
          ))}
        </SettingsSelect>
      </SettingRow>
      <SettingRow>
        <label style={{ margin: 'auto 0' }}>
          {t('avatar.configs.model', 'Model')}:
        </label>
        <SettingsSelect
          value={currentModel || ''}
          onChange={(e) =>
            handleSettingsChange({
              ...settings,
              model: e.target.value as Model,
            })
          }
        >
          {Object.keys(models).map((modelName: string) => (
            <option key={modelName} value={modelName}>
              {get(models, `${modelName}.name`, '')}
            </option>
          ))}
        </SettingsSelect>
      </SettingRow>
    </SettingsContainer>
  );
};

type SettingsModalProps = SettingsModalContentProps & {
  isOpen: boolean;
};

export const SettingsModal: FC<SettingsModalProps> = ({
  isOpen,
  settings,
  mainStateDispatch,
  hasGoogleApiKey,
}) => {
  return (
    <>
      {isOpen && (
        <Modal
          onClose={() => mainStateDispatch({ type: 'TOGGLE_SETTINGS_MODAL' })}
        >
          <SettingsModalContent
            mainStateDispatch={mainStateDispatch}
            settings={settings}
            hasGoogleApiKey={hasGoogleApiKey}
          />
        </Modal>
      )}
    </>
  );
};

const SettingsContainer = styled.div`
  padding-top: 30px;
  width: 600px;
  max-width: 100%;
`;

const SettingRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  max-width: 100%;

  & label {
    width: 120px; // Set fixed width for labels
    flex-shrink: 0; // Ensure the label doesn't shrink below 200px
    margin-bottom: auto;
  }

  & select,
  & textarea {
    flex-grow: 1; // Allow the input and textarea to take up the remaining space
    width: calc(100% - 200px); // Subtract the width of the label
  }
`;

const SettingsTextArea = styled.textarea`
  border: 1px solid #ccc;
`;

const SettingsSelect = styled.select`
  border: 1px solid #ccc;
  padding: 4px;
`;
